import React, { useState, useEffect } from 'react';
import { Box, Typography, Fade } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import StarsIcon from '@mui/icons-material/Stars';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PsychologyIcon from '@mui/icons-material/Psychology';

const STEPS = [
    {
        label: 'Analyzing skills & expertise',
        description: 'Evaluating answers to identify key competencies',
        icon: <AutoAwesomeIcon sx={{ fontSize: 20 }} />,
        duration: 3000,
    },
    {
        label: 'Writing professional story',
        description: 'Crafting a compelling narrative from experience',
        icon: <AutoStoriesIcon sx={{ fontSize: 20 }} />,
        duration: 4500,
    },
    {
        label: 'Creating key highlights',
        description: 'Selecting the most impactful qualities',
        icon: <StarsIcon sx={{ fontSize: 20 }} />,
        duration: 3000,
    },
    {
        label: 'Building CV layout',
        description: 'Assembling the final document',
        icon: <DescriptionIcon sx={{ fontSize: 20 }} />,
        duration: 2500,
    },
];

const AnimatedDots: React.FC = () => (
    <Box component="span" sx={{ ml: 0.3, letterSpacing: '0.05em' }}>
        {[0, 1, 2].map(i => (
            <Box
                key={i}
                component="span"
                sx={{
                    animationName: 'dotPulse',
                    animationDuration: '1.4s',
                    animationTimingFunction: 'ease-in-out',
                    animationIterationCount: 'infinite',
                    animationDelay: `${i * 0.2}s`,
                    '@keyframes dotPulse': {
                        '0%, 80%, 100%': { opacity: 0.15 },
                        '40%': { opacity: 1 },
                    },
                }}
            >
                .
            </Box>
        ))}
    </Box>
);

const StepRow: React.FC<{
    step: typeof STEPS[number];
    completed: boolean;
    active: boolean;
    pending: boolean;
}> = ({ step, completed, active, pending }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1,
            px: 1.5,
            borderRadius: 2,
            bgcolor: active ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
            transition: 'all 0.4s ease',
        }}
    >
        {/* Step icon circle */}
        <Box
            sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: completed
                    ? 'success.main'
                    : active
                    ? 'primary.main'
                    : 'grey.200',
                color: completed || active ? '#fff' : 'grey.400',
                transition: 'all 0.4s ease',
                ...(active && {
                    animation: 'stepGlow 2s ease-in-out infinite',
                    '@keyframes stepGlow': {
                        '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)' },
                        '70%': { boxShadow: '0 0 0 8px rgba(25, 118, 210, 0)' },
                        '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                    },
                }),
                ...(completed && {
                    animation: 'checkPop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    '@keyframes checkPop': {
                        '0%': { transform: 'scale(0.5)' },
                        '100%': { transform: 'scale(1)' },
                    },
                }),
            }}
        >
            {completed ? (
                <CheckCircleIcon sx={{ fontSize: 20 }} />
            ) : (
                step.icon
            )}
        </Box>

        {/* Step text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
                variant="body2"
                fontWeight={active ? 600 : completed ? 500 : 400}
                color={
                    completed
                        ? 'success.dark'
                        : pending
                        ? 'text.disabled'
                        : 'text.primary'
                }
                sx={{ transition: 'all 0.3s ease' }}
            >
                {step.label}
                {active && <AnimatedDots />}
            </Typography>
            {active && (
                <Fade in timeout={400}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {step.description}
                    </Typography>
                </Fade>
            )}
        </Box>
    </Box>
);

const CVGenerationLoading: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<number[]>([]);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        // Trigger entrance animation
        const mountTimer = setTimeout(() => setMounted(true), 50);

        const timeouts: ReturnType<typeof setTimeout>[] = [mountTimer];
        let elapsed = 0;

        STEPS.forEach((_, index) => {
            if (index === 0) return;
            elapsed += STEPS[index - 1].duration;
            timeouts.push(setTimeout(() => {
                setCompletedSteps(prev => [...prev, index - 1]);
                setActiveStep(index);
            }, elapsed));
        });

        return () => timeouts.forEach(t => clearTimeout(t));
    }, []);

    const isCompleted = (index: number) => completedSteps.includes(index);
    const isActive = (index: number) => activeStep === index;
    const isPending = (index: number) => !isCompleted(index) && !isActive(index);

    const progress = ((completedSteps.length + 0.5) / STEPS.length) * 100;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 4,
                background: 'linear-gradient(145deg, #f8fafc 0%, #eef2f7 50%, #e8edf5 100%)',
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.5s ease, transform 0.5s ease',
            }}
        >
            {/* Hero AI icon with orbital rings */}
            <Box sx={{ position: 'relative', mb: 3.5 }}>
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
                        animation: 'heroFloat 3s ease-in-out infinite',
                        '@keyframes heroFloat': {
                            '0%, 100%': { transform: 'translateY(0)', boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)' },
                            '50%': { transform: 'translateY(-6px)', boxShadow: '0 8px 30px rgba(25, 118, 210, 0.45)' },
                        },
                    }}
                >
                    <PsychologyIcon sx={{ fontSize: 38, color: '#fff' }} />
                </Box>
                {/* Inner spinning ring */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -6,
                        left: -6,
                        width: 84,
                        height: 84,
                        borderRadius: '50%',
                        border: '2px solid transparent',
                        borderTopColor: 'primary.light',
                        animation: 'ringA 2s linear infinite',
                        '@keyframes ringA': {
                            from: { transform: 'rotate(0deg)' },
                            to: { transform: 'rotate(360deg)' },
                        },
                    }}
                />
                {/* Outer counter-spinning ring */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -10,
                        left: -10,
                        width: 92,
                        height: 92,
                        borderRadius: '50%',
                        border: '1.5px solid transparent',
                        borderBottomColor: 'rgba(25, 118, 210, 0.2)',
                        borderLeftColor: 'rgba(25, 118, 210, 0.12)',
                        animation: 'ringB 3s linear infinite reverse',
                        '@keyframes ringB': {
                            from: { transform: 'rotate(0deg)' },
                            to: { transform: 'rotate(360deg)' },
                        },
                    }}
                />
            </Box>

            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, letterSpacing: '-0.02em' }}>
                Generating Your CV
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                AI is analyzing and composing your professional profile
            </Typography>

            {/* Step list */}
            <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {STEPS.map((step, index) => (
                    <StepRow
                        key={index}
                        step={step}
                        completed={isCompleted(index)}
                        active={isActive(index)}
                        pending={isPending(index)}
                    />
                ))}
            </Box>

            {/* Progress bar */}
            <Box sx={{ width: '100%', maxWidth: 400, mt: 3.5, px: 1.5 }}>
                <Box
                    sx={{
                        height: 3,
                        borderRadius: 2,
                        bgcolor: 'grey.200',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            height: '100%',
                            borderRadius: 2,
                            background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                            width: `${progress}%`,
                            transition: 'width 0.8s ease-in-out',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                                animation: 'barShimmer 1.5s ease-in-out infinite',
                                '@keyframes barShimmer': {
                                    '0%': { transform: 'translateX(-100%)' },
                                    '100%': { transform: 'translateX(100%)' },
                                },
                            },
                        }}
                    />
                </Box>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1.5, display: 'block', textAlign: 'center', opacity: 0.6 }}
                >
                    This usually takes 10–15 seconds
                </Typography>
            </Box>
        </Box>
    );
};

export default CVGenerationLoading;
